import React, { useState } from 'react';
import { Button, Form, FormControl, Modal, Spinner } from 'react-bootstrap-5';
import IncludeOwnerDataCheckbox, { type IncludeOwnerDataCheckboxProps } from './include_owner_data_checkbox';
import { fetchOwners, createRethinkListWithProperty, createRethinkList } from '../../../helpers/prospect_api';
import { displayToast } from 'bundles/connect/helpers/display_toast';

type CreateRethinkListProps = {
  bulk?: boolean;
  onBulkInsert?: (rethinkListId: string | undefined,
    email: string,
    sendEmail: boolean,
    listName: string
  ) => Promise<void>;
  onHide: () => void;
  ownerCheckboxProps: IncludeOwnerDataCheckboxProps;
  insightsPropertyId: string;
};

export default function CreateRethinkList({ bulk = false,
  insightsPropertyId,
  ownerCheckboxProps,
  onHide,
  onBulkInsert }: CreateRethinkListProps) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  const handleModalDismiss = () => {
    setName('');
    setDesc('');
    onHide();
  };

  const handleEmailCheck = () => {
    setSendEmail(!sendEmail);
    setEmail('');
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (bulk) {
      return await createRethinkList({ name, desc, bulk: Boolean(onBulkInsert) }).then(async (res) => {
        if (onBulkInsert) {
          await onBulkInsert(res.rethinkListId, email, sendEmail, name);
          setLoading(false);
          displayToast('List job queued', 'success');
        }
      });
    }

    if (ownerCheckboxProps.checked) {
      await fetchOwners(insightsPropertyId, true, false);
    }

    await createRethinkListWithProperty({
      name,
      desc,
      insightsPropertyId,
      includeContacts: ownerCheckboxProps.checked,
    }).then(() => {
      displayToast('List successfully created', 'success');
      setLoading(false);
      handleModalDismiss();
    })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Modal.Header className="flex-row">
        <Modal.Title>
          Add to New List
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label>List Name</Form.Label>
        <Form.Control
          className="mb-2"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <Form.Label>Description</Form.Label>
        <FormControl
          as="textarea"
          className="mb-2"
          rows={3}
          value={desc}
          onChange={e => setDesc(e.target.value)}
        />
        <Form.Label>List Options</Form.Label>
        <IncludeOwnerDataCheckbox {...ownerCheckboxProps} />
        {bulk &&
          (<div className="mx-2">
            <Form.Check
              checked={sendEmail}
              id="send-email-rethink-list"
              label="Email me when list creation is complete"
              type="checkbox"
              onChange={handleEmailCheck}
            />
            {sendEmail && (
              <div className="m-2">
                <Form.Label className="mt-2">Email address</Form.Label>
                <Form.Control
                  className="mt-2"
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            )}
          </div>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={handleModalDismiss}>
          Cancel
        </Button>
        <Button disabled={loading} variant="secondary" onClick={() => handleSubmit()}>
          Create List
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </Button>
      </Modal.Footer>
    </>
  );
}
